import { CollapsableTable } from '@/components/uiKit/Tables/Collpsable';
import React, { useMemo } from 'react';
import { useOilReportTable, useSelectedCoordinate } from '@/hooks';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { cn } from '@/lib/utils';
import { generateTableStructure } from './util';
import { useTranslation } from 'react-i18next';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { CircleInfoIcon, OilCanDripIcon, SquarePollHorizontalIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';

const iconClassName = 'w-10 h-10 min-w-10 min-h-10 rounded-full bg-gray-200 flex items-center justify-center';

interface OilReportTableProps {
  fromDate?: any;
  toDate?: any;
  oilReportData?: any;
  isLoading: boolean;
  clickedCoordinate?: string;
  component?: any;
}
// @TODO: replace solution for component selection
// @TODO: replace useEffects

const OilReportTableEmptyState = () => {
  return (
    <div className="h-[calc(100vh-28rem)] w-full flex items-center justify-center flex-col gap-6">
      <div className="font-semibold">No Oil Reports Found</div>
      <div>Oil analysis enhances machine health insights - share your reports, and we’ll add them!</div>
    </div>
  );
};

export const OilReportTable = ({ oilReportData, isLoading = true, component }: OilReportTableProps) => {
  const { t } = useTranslation();
  const selectedReportTitle = t('assetStudio.reportsData.oilTable.selectedReport');
  const oilTypeTitle = t('assetStudio.reportsData.oilTable.oilType');
  const reportRecommendationsTitle = t('assetStudio.reportsData.oilTable.reportRecommendations');
  const selectedOilReportMetadata = useSelector((state: RootState) => state.application.selectedOilReportMetadata);
  const coordinates = useMemo(() => (oilReportData ? Object.keys(oilReportData) : []), [oilReportData]);
  const { selectedCoordinate, setSelectedCoordinate } = useSelectedCoordinate(coordinates, component);

  const oilReport = useMemo(
    () => (coordinates.length > 0 ? oilReportData[selectedCoordinate] : []),
    [selectedCoordinate, oilReportData, coordinates, isLoading]
  );
  const reportStructure = useMemo(() => generateTableStructure(oilReport), [oilReport]);
  const { columns, expandedRows, setExpandedRows, renderNestedRowCell, selectedItem } = useOilReportTable(
    reportStructure,
    oilReport,
    selectedOilReportMetadata
  );

  const selectedEl = selectedItem
    ? oilReport?.find((el: any) => el.meta.date === selectedItem)?.meta
    : selectedOilReportMetadata;

  if (!isLoading && (!oilReport || oilReport.length < 1)) return <OilReportTableEmptyState />;

  const hasCoordinates = coordinates && coordinates.length > 1;
  const tableCardHeight = hasCoordinates ? 'min-h-[calc(100vh-20.8rem)]' : 'min-h-[calc(100vh-14rem)]';
  const tableMaxHeight = hasCoordinates ? 'max-h-[calc(100vh-17.5rem)]' : 'max-h-[calc(100vh-14.6rem)]';

  return (
    <div className="flex gap-2 h-[calc(100vh-10.3rem)] overflow-y-hidden w-full">
      {/* <FilterSideMenu items={maintenanceData} onFilterChange={handleFilterChange} title={<div>Placeholder</div>} /> */}
      {isLoading ? (
        <Skeleton className="w-full min-h-[calc(100vh-10.3rem)] h-[calc(100vh-10.3rem)]" />
      ) : (
        <div className="flex flex-col w-full h-100 gap-2 overflow-x-auto overflow-hidden">
          {coordinates && coordinates.length > 1 && (
            <div className="flex w-full gap-4 py-1 min-h-9 max-h-9">
              {/* {coordinates && coordinates.length > 1 ? ( */}
              {coordinates?.map((el: any) => (
                <div
                  key={el}
                  onClick={() => setSelectedCoordinate(el)}
                  className={cn(
                    'border-b-2 border-transparent px-2 cursor-pointer',
                    selectedCoordinate === el ? 'font-semibold border-primary-600' : ''
                  )}
                  data-testid={`coordination_${generateTestId(el)}`}
                >
                  {el}
                </div>
              ))}
              {/* ) : (
                <div className={cn('border-b-2 border-transparent px-2 cursor-pointer')}>
                  {component?.component_ui_name}
                </div>
              )} */}
            </div>
          )}
          <div className="flex gap-6 items-center sticky left-0">
            <div className="flex items-center gap-2">
              <div className={iconClassName}>
                <Icon icon={SquarePollHorizontalIcon} />
              </div>
              <div className="">
                <div className="text-xs">{selectedReportTitle}</div>
                <div
                  className="font-semibold text-xs"
                  data-testid={`bottom_line_${generateTestId(selectedReportTitle)}`}
                >
                  {`${selectedEl?.date && format(new Date(selectedEl?.date), 'dd MMM yy')},
              Report Number ${selectedEl?.report_number}`}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className={iconClassName}>
                <Icon icon={OilCanDripIcon} />
              </div>
              <div className="">
                <div className="text-xs">{oilTypeTitle}</div>
                <div
                  className="font-semibold text-xs"
                  data-testid={`bottom_line_${generateTestId(oilTypeTitle)}`}
                >{`${selectedEl?.oil_type}`}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className={iconClassName}>
                <Icon icon={CircleInfoIcon} />
              </div>
              <div className="">
                <div className="text-xs">{reportRecommendationsTitle}</div>
                <div
                  className="font-semibold text-xs"
                  data-testid={`bottom_line_${generateTestId(reportRecommendationsTitle)}`}
                >{`${selectedEl?.report_recommendations}`}</div>
              </div>
            </div>
          </div>
          <CollapsableTable
            scrollToColumnId={selectedItem}
            mainData={reportStructure}
            renderNestedRowCell={renderNestedRowCell}
            nestedDataKey={'data'}
            mainColumns={columns}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            classNames={{
              baseCard: {
                card: `rounded-lg overflow-hidden px-3 py-0 max-w-[calc(100vw-29.5rem)] ${tableCardHeight} `,
                content: `max-w-100 overflow-hidden p-0 pb-2 bg-gray-200 ${tableCardHeight}`,
              },
              table: { container: `overflow-auto  max-w-100 ${tableMaxHeight}` },
            }}
          />
        </div>
      )}
    </div>
  );
};
